import { useByoDataContext } from "@pages/Byo/ByoContext";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from "react";
import {
    ByoAnalyticType,
    ByoValueType,
    ByoWord,
    ConsumersEnum,
    GROWTH_SLICES,
    Slice,
    SLICES,
    SlicesEnum
} from "@models/Byo";
import { useCurrentCountryCode, useCurrentCurrency } from "@vms/Country/useCurrentCountryCode";
import { equalInLC, getFormattedThousand } from "@datassential/platform-ui-lib";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import { useRestaurantMenu } from "@vms/RestaurantMenu";
import { RestaurantMenuContextType } from "@vms/RestaurantMenu/RestaurantMenuContext";
import { ByoChartDataItem, ByoTableColumn, ByoTableItem } from "./components/ByoTable/byoTable.helpers";
import { sortByName } from "@core/old_helpers";
import { getFormattedThousandWithDecimal, toFiniteOrNull } from "@core/helpers";

type TableChartSlicesEnum = 
    | SlicesEnum.Period 
    | SlicesEnum.PredictPeriod 
    | SlicesEnum.PeriodQuarter 
    | SlicesEnum.PredictPeriodQuarter;

export type ByoTableData = {
    slices: Slice[];
    columns: ByoTableColumn[];
    data: ByoTableItem[];
    consumerAccessRestricted: boolean;
    sortingKey: string;
    sortingDirection: Sort;
    setSortingKey: Dispatch<SetStateAction<string>>;
    setSortingDirection: Dispatch<SetStateAction<Sort>>;
};

const getColumnSortKey = (sliceId: SlicesEnum, columnId: number): string => {
    return `${sliceId}-${columnId}`;
}

const darkColumnsSlices = [
    SlicesEnum.Total,
    ...GROWTH_SLICES,
];

function getColumnsBySlices (
    rawData: ByoWord["tables"],
    slices: SlicesEnum[],
    tooltipsData: RestaurantMenuContextType["tooltipsData"],
    activeAnalyticType: ByoAnalyticType,
): ByoTableColumn[] {
    return slices.flatMap((sliceId) => {
        let columns: ByoTableColumn[] = (rawData ?? [])
            .filter(cell => cell.slice === sliceId)
            .map((cell): ByoTableColumn => {
                const tooltipData = tooltipsData.find(
                    tooltip => tooltip.sliceId === sliceId && tooltip.id === cell.id
                );

                // Number('') and Number(null) are 0, so an absent base size has to be
                // rejected explicitly rather than rendered as a real 0.
                const footerContent = toFiniteOrNull(cell.baseSize);

                let footerContentString = '';

                if (footerContent === null) {
                    footerContentString = 'N/A';
                }
                else if (activeAnalyticType === ByoAnalyticType.Count) {
                    footerContentString = getFormattedThousandWithDecimal(footerContent, 1);
                }
                else {
                    footerContentString = getFormattedThousand(footerContent);
                }

                return {
                    id: cell.id ?? 0,
                    sliceId,
                    key: `${sliceId}-${cell.id}`,
                    sortKey: getColumnSortKey(sliceId, cell.id ?? 0),
                    name: cell.name,
                    isClickable: true,
                    footerContent: footerContentString,
                    isDark: darkColumnsSlices.includes(sliceId),
                    isMedium: sliceId === SlicesEnum.PredictedFourYearTrend,
                    tooltipTitle: tooltipData?.title,
                    tooltipDescription: tooltipData?.description,
                    tooltipImageUrl: tooltipData?.imageUrl,
                };
            });

        const slice = SLICES.find(s => s.id === sliceId);

        if (slice?.columnsOrder) {
            columns.sort((a, b) => {
                const indexA = slice.columnsOrder?.indexOf(a.id) ?? -1;
                const indexB = slice.columnsOrder?.indexOf(b.id) ?? -1;
                return indexA - indexB;
            });
        }

        if (sliceId === SlicesEnum.Consumers) {
            columns = [
                {
                    id: ConsumersEnum.KnowIt,
                    sliceId,
                    key: `${sliceId}-${ConsumersEnum.KnowIt}`,
                    sortKey: getColumnSortKey(sliceId, ConsumersEnum.KnowIt),
                    name: '% know it',
                    footerContent: '-',
                },
                {
                    id: ConsumersEnum.HaveTriedIt,
                    sliceId,
                    key: `${sliceId}-${ConsumersEnum.HaveTriedIt}`,
                    sortKey: getColumnSortKey(sliceId, ConsumersEnum.HaveTriedIt),
                    name: '% have tried it',
                    footerContent: '-',
                },
                {
                    id: ConsumersEnum.LoveOrLikeIt,
                    sliceId,
                    key: `${sliceId}-${ConsumersEnum.LoveOrLikeIt}`,
                    sortKey: getColumnSortKey(sliceId, ConsumersEnum.LoveOrLikeIt),
                    name: '% love or like it',
                    footerContent: '-',
                },
            ];
        }

        return columns;
    });
}

/**
 * The fields getColumnValue actually reads. realValue/realCount are deliberately
 * wider than the generated schema's `number`: a non-finite float can reach the
 * client as its string spelling ("NaN" / "Infinity"), which is exactly what the
 * guard below exists to reject.
 */
type ByoColumnValueCell = {
    slice?: SlicesEnum;
    id?: number;
    realValue?: number | string | null;
    realCount?: number | string | null;
};

type ByoColumnValueSource = {
    activeTables?: ByoColumnValueCell[];
    flavorData?: ByoWord["flavorData"];
};

function getColumnValue(
    word: ByoColumnValueSource,
    column: ByoTableColumn,
    activeValueType: ByoValueType,
): number | null {
    if (column.sliceId === SlicesEnum.Consumers) {
        switch (column.id) {
            case ConsumersEnum.KnowIt:
                return word.flavorData?.data?.knowIt ?? null;
            case ConsumersEnum.HaveTriedIt:
                return word.flavorData?.data?.haveTriedIt ?? null;
            case ConsumersEnum.LoveOrLikeIt:
                return word.flavorData?.data?.loveOrLikeIt ?? null;
            default:
                return null;
        }
    }

    const cell = word.activeTables?.find(
        table => table.slice === column.sliceId && table.id === column.id
    );

    // Growth slices are percentages whatever the selected value type.
    const usePct = activeValueType === ByoValueType.Pct
        || GROWTH_SLICES.includes(column.sliceId);

    // Both fields share one guard on purpose: the backend can report a non-finite
    // float for either, and an unguarded one formats as a plausible-looking 0.0.
    return toFiniteOrNull(usePct ? cell?.realValue : cell?.realCount);
}

export function useByoTableData(): ByoTableData {
    const { tooltipsData } = useRestaurantMenu();
    const {
        data: rawData,
        activeSliceId,
        setActiveSliceId,
        activeAnalyticsType,
        activeValueType,
    } = useByoDataContext();
    const currentCountry = useCurrentCountryCode();
    const currency = useCurrentCurrency();

    const slices = useMemo(
        () => rawData.length > 0 && rawData.some(i => !i.isLoading)
            ? SLICES.filter(slice => {
                let isVisible = slice.isTableSlice;

                if (slice.visibleForCountries?.length) {
                    isVisible = isVisible && slice.visibleForCountries.some(
                        countryCode => equalInLC(countryCode, currentCountry)
                    );
                }

                if (slice.id !== SlicesEnum.Consumers) {
                    isVisible = isVisible && rawData[0]?.tables?.some(
                        cell => cell.slice === slice.id
                    );
                }

                return isVisible;
            })
            : [],
        [rawData, currentCountry]
    );

    useEffect(() => {
        if (!slices.some(i => i.id === activeSliceId) && slices[0]) {
            setActiveSliceId(slices[0].id);
        }
    }, [slices, setActiveSliceId, activeSliceId]);

    const activeSlice = useMemo(
        () => slices.find(slice => slice.id === activeSliceId),
        [slices, activeSliceId]
    );

    const dataWithActiveTables = useMemo(
        () => rawData.map(word => {
            const tables = word.tables?.filter(
                table => table.analyticsType === activeAnalyticsType
            ) || [];

            return {
                ...word,
                activeTables: tables,
            };
        }),
        [rawData, activeAnalyticsType]
    );

    const dataWithChartData = useMemo(
        () => dataWithActiveTables.map(word => {
            const sliceMap: Record<TableChartSlicesEnum, ByoChartDataItem[]> = {
                [SlicesEnum.Period]: [],
                [SlicesEnum.PredictPeriod]: [],
                [SlicesEnum.PeriodQuarter]: [],
                [SlicesEnum.PredictPeriodQuarter]: [],
            };
            
            (word.activeTables ?? []).forEach((cell) => {
                const sliceKey = cell.slice as TableChartSlicesEnum;
                if (!sliceMap[sliceKey]) return;

                sliceMap[sliceKey].push({
                    id: cell.id ?? 0,
                    name: cell.name ?? '',
                    value: cell.value ?? '',
                    valueNumeric: cell.realValue ?? Number(cell.value || 0),
                    baseSize: cell.baseSize ?? '',
                });
            });

            const chartData = {
                actualData: sortByName(sliceMap[SlicesEnum.Period]),
                predictedData: sortByName(sliceMap[SlicesEnum.PredictPeriod]),
                actualDataQuarters: sortByName(sliceMap[SlicesEnum.PeriodQuarter]),
                predictedDataQuarters: sortByName(sliceMap[SlicesEnum.PredictPeriodQuarter]),
            };

            return {
                ...word,
                chartData,
            };
        }),
        [dataWithActiveTables]
    );

    const columns = useMemo(
        () => getColumnsBySlices(
            dataWithActiveTables[0]?.activeTables,
            activeSlice?.columns ?? [],
            tooltipsData,
            activeAnalyticsType,
        ),
        [activeSlice, dataWithActiveTables, tooltipsData]
    );

    const totalColumn = useMemo(
        () => columns.find(column => column.sliceId === SlicesEnum.Total),
        [columns]
    );

    const formatValue = useCallback(
        (value: number | null, sliceId: SlicesEnum) => {
            if (GROWTH_SLICES.includes(sliceId)) {
                if (value === null) {
                    return 'N/A';
                }
                return `${value > 0 ? '+' : ''}${getFormattedThousand(Math.round(value))}%`;
            }

            if (value === null) {
                return '-';
            }

            if (activeAnalyticsType === ByoAnalyticType.Price) {
                return `${currency} ${getFormattedThousandWithDecimal(value)}`;
            }

            if (activeAnalyticsType === ByoAnalyticType.Count) {
                return `${getFormattedThousandWithDecimal(value, 1)}`;
            }

            if (activeValueType === ByoValueType.Count) {
                return getFormattedThousand(value);
            }
            return `${value.toFixed(1)}%`;
        },
        [activeAnalyticsType, currency, activeValueType]
    );

    const dataWithColumnsValues = useMemo(
        () => dataWithChartData.map(word => {
            const columnsValues = columns.map(
                column => getColumnValue(word, column, activeValueType)
            );

            return {
                ...word,
                columnsValues,
            };
        }),
        [dataWithChartData, columns, activeValueType]
    );

    const dataForSorting = useMemo(
        () => dataWithColumnsValues.map(word => {
            const columnsKeysValues: { [key: string]: number | null } = {};

            columns.forEach((column, index) => {
                columnsKeysValues[column.sortKey ?? ''] = word.columnsValues[index];
            });

            let macStage = word.keyword.macStage ?? null;
            if (macStage === 0) {
                macStage = null;
            }

            return {
                ...word,
                ...columnsKeysValues,
                macStage,
            };
        }),
        [dataWithColumnsValues, columns]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        dataForSorting,
        "isLoading",
        Sort.Desc,
    );

    useEffect(() => {
        if (totalColumn?.sortKey) {
            setSortingKey(totalColumn.sortKey as keyof typeof dataForSorting[0]);
        } else {
            setSortingKey('isLoading');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalColumn?.sortKey]);

    const dataWithColumnsValuesStrings = useMemo(
        () => sortedData.map(word => {
            const columnsValues: string[] = [];

            columns.forEach((column, index) => {
                columnsValues[index] = formatValue(word.columnsValues[index], column.sliceId);
            });

            return {
                ...word,
                columnsValues,
            };
        }),
        [sortedData, columns, formatValue]
    );

    const consumerAccessRestricted = useMemo(() => {
        const hasAccess = rawData[0]?.flavorData?.accessLevel !== 0;

        return !hasAccess && activeSliceId === SlicesEnum.Consumers;
    }, [rawData, activeSliceId]);

    return {
        slices,
        columns,
        data: dataWithColumnsValuesStrings,
        consumerAccessRestricted,
        sortingKey,
        sortingDirection,
        setSortingKey: setSortingKey as Dispatch<SetStateAction<string>>,
        setSortingDirection,
    };
}